import ResponsiveTable from "@/components/ResponsiveTable";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const TABS = [
  { v: "reservations", l: "Rezervasyonlar" },
  { v: "deposits", l: "Kaporalar" },
  { v: "sales", l: "Satışlar" },
];

export default function Sales() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "sales" ? "sales" : "reservations");
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") === "expired" ? "__expired" : searchParams.get("status") === "expiring_today" ? "__expiring_today" : searchParams.get("status") || "");
  const urlStatus = searchParams.get("status") || "";
  const urlTab = searchParams.get("tab") || "";
  const periodFilter = searchParams.get("period") || "all";

  const load = useCallback(async () => {
    let url = `/${tab}`;
    const params = {};
    if (tab === "reservations" && statusFilter && !["__expired", "__expiring_today"].includes(statusFilter)) params.status = statusFilter;
    if (tab === "reservations" && ["__expired", "__expiring_today"].includes(statusFilter)) params.status = "Aktif";
    try {
      const { data } = await api.get(url, { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [tab, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setStatusFilter(urlStatus === "expired" ? "__expired" : urlStatus === "expiring_today" ? "__expiring_today" : urlStatus);
    if (urlTab === "sales") setTab("sales");
    else if (urlTab === "deposits") setTab("deposits");
    else if (urlTab === "reservations" || ["expired", "expiring_today"].includes(urlStatus)) setTab("reservations");
  }, [urlStatus, urlTab]);

  const today = new Date().toISOString().slice(0, 10);
  const periodStart = (() => {
    const now = new Date();
    if (periodFilter === "today") return `${today}T00:00:00`;
    if (["week", "month", "30d"].includes(periodFilter)) {
      now.setUTCDate(now.getUTCDate() - (periodFilter === "week" ? 7 : 30));
      return now.toISOString();
    }
    return "";
  })();
  const enriched = items.map((item) => ({
    ...item,
    _expired: tab === "reservations" && item.status === "Aktif" && item.valid_until?.slice(0, 10) < today,
  })).filter((item) => {
    if (tab === "reservations" && statusFilter === "__expired") return item._expired;
    if (tab === "reservations" && statusFilter === "__expiring_today") return item.status === "Aktif" && item.valid_until?.slice(0, 10) === today;
    if (tab === "sales") return (!statusFilter || item.status === statusFilter) && (!periodStart || item.sale_date >= periodStart);
    return true;
  });

  const chooseTab = (value) => {
    setTab(value);
    setStatusFilter("");
    setSearchParams({ tab: value }, { replace: true });
  };
  const chooseStatus = (value) => {
    setStatusFilter(value);
    const params = { tab };
    if (value) params.status = value === "__expired" ? "expired" : value === "__expiring_today" ? "expiring_today" : value;
    if (tab === "sales" && periodFilter !== "all") params.period = periodFilter;
    setSearchParams(params, { replace: true });
  };

  const expireToSale = async (r) => {
    try {
      await api.post(`/reservations/${r.id}/return-to-sale`);
      toast.success("Rezervasyon kapatıldı, bölüm satışta");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelReservation = async (r) => {
    const reason = window.prompt("İptal nedeni:", "");
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/reservations/${r.id}/cancel`, { reason });
      toast.success("Rezervasyon iptal edildi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-display">Rezervasyon &amp; Satış</h1>
        <p className="text-slate-500 mt-1">Rezervasyon, kapora ve satışları takip edin.</p>
      </div>

      <div className="border-b border-slate-200 overflow-x-auto">
        <div className="flex w-max min-w-full gap-2">
          {TABS.map((t) => (
            <button key={t.v} onClick={() => chooseTab(t.v)}
                    className={`whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition ${tab === t.v ? "border-amber-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                    data-testid={`tab-${t.v}`}>{t.l}</button>
          ))}
        </div>
      </div>

      {tab === "reservations" && (
        <div className="flex items-center gap-2">
          <select value={statusFilter} onChange={(e) => chooseStatus(e.target.value)}
                  className="input w-auto" data-testid="reservation-status-filter">
            <option value="">Tüm Durumlar</option>
            <option value="__expired">Süresi Geçen Aktif</option>
            <option value="__expiring_today">Bugün Süresi Dolacak Aktif</option>
            {["Aktif", "Süresi Doldu", "İptal Edildi", "Kapora Alındı", "Satışa Döndü"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      )}

      {tab === "sales" && (
        <div className="flex items-center gap-2 flex-wrap">
          <select value={statusFilter} onChange={(e) => chooseStatus(e.target.value)}
                  className="input w-auto" aria-label="Satış durumu" data-testid="sale-status-filter">
            <option value="">Tüm Durumlar</option>
            <option value="Aktif">Aktif</option>
            <option value="İptal Edildi">İptal Edildi</option>
          </select>
          <select value={periodFilter} onChange={(e) => {
            const params = { tab: "sales" };
            if (statusFilter) params.status = statusFilter;
            if (e.target.value !== "all") params.period = e.target.value;
            setSearchParams(params, { replace: true });
          }} className="input w-auto" aria-label="Satış dönemi" data-testid="sale-period-filter">
            <option value="all">Tüm Zamanlar</option>
            <option value="today">Bugün</option>
            <option value="week">Bu Hafta</option>
            <option value="month">Bu Ay</option>
            <option value="30d">Son 30 Gün</option>
          </select>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden overflow-x-auto">
        <ResponsiveTable className="w-full min-w-[850px] text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Bölüm</th>
              <th className="text-left px-4 py-3">Müşteri</th>
              <th className="text-left px-4 py-3">Personel</th>
              {tab === "reservations" && <th className="text-left px-4 py-3">Geçerlilik</th>}
              {(tab === "deposits" || tab === "sales") && <th className="text-left px-4 py-3">Tutar</th>}
              {tab === "sales" && <th className="text-left px-4 py-3">Ödeme</th>}
              <th className="text-left px-4 py-3">Durum</th>
              <th className="text-left px-4 py-3">Tarih</th>
              <th className="text-left px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {enriched.map((i) => (
              <tr key={i.id} className="border-t border-slate-100" data-testid={`${tab}-row-${i.id}`}>
                <td className="px-4 py-3">
                  <Link to={`/units/${i.unit_id}`} className="font-semibold text-slate-900 hover:text-amber-600">
                    {i.unit_no || "—"}
                  </Link>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {i.project_name || "Proje bilgisi yok"} / {i.block_name || "Blok bilgisi yok"} / {i.floor_name || "Kat bilgisi yok"}
                  </div>
                  {i.unit_room_type && <div className="text-xs text-slate-400">{i.unit_room_type}</div>}
                </td>
                <td className="px-4 py-3">{i.customer_name || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{i.sales_agent_name || "—"}</td>
                {tab === "reservations" && (
                  <td className="px-4 py-3 text-sm">
                    {i.valid_until?.slice(0, 10) || "—"}
                    {i._expired && <span className="ml-2 text-xs text-red-600 font-semibold">Süresi Doldu</span>}
                  </td>
                )}
                {(tab === "deposits" || tab === "sales") && (
                  <td className="px-4 py-3 font-semibold">{Number(i.amount || i.sale_price || 0).toLocaleString("tr-TR")} {i.currency}</td>
                )}
                {tab === "sales" && <td className="px-4 py-3 text-sm">{i.payment_type}</td>}
                <td className="px-4 py-3 text-sm">{i.status}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{(i.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  {tab === "reservations" && i.status === "Aktif" && (
                    <div className="flex gap-1">
                      {i._expired && user?.role !== "agent" && (
                        <button onClick={() => expireToSale(i)} className="text-xs px-2 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded" data-testid={`expire-reservation-${i.id}`}>
                          Stoğa Döndür
                        </button>
                      )}
                      {user?.role !== "agent" && (
                        <button onClick={() => cancelReservation(i)} className="text-xs px-2 py-1 bg-white border border-slate-200 hover:bg-slate-50 rounded">İptal</button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {enriched.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">Kayıt yok</td></tr>
            )}
          </tbody>
        </ResponsiveTable>
      </div>
    </div>
  );
}
